<template>
  <div class="">
    <div class="video-container" :class="{'hide-video-container': !isShowVideo }">
      <video controls class="video-player" :controlsList="$store.state.appConfig.is_embedded_in_mh_app ? 'nodownload' : ''" @play="handlePlay">
        <source :src="videoSrc" type="video/webm" />
        Your browser does not support the video tag
      </video>
      <div class="download-section">
        <a href="#" @click="downloadVideo" class="download-btn" 
          v-click-track="'Iframe.ancientDnaAncientOriginsBreakdownDownloadVideoClick'">
          <DownloadIcon /> {{$tI_G('Download video')}}
        </a>
      </div>
    </div>
    <div class="toggle-section">
      <div @click="isShowVideo = !isShowVideo" class="toggle-btn">
        <span v-show="isShowVideo" v-click-track="'Iframe.ancientDnaAncientOriginsBreakdownHideVideoClick'">
          {{$tI_G('Hide video')}} <MenuTopIcon />
        </span>
        <span v-show="!isShowVideo" v-click-track="'Iframe.ancientDnaAncientOriginsBreakdownShowVideoClick'">
          {{$tI_G('Show video')}} <MenuDownIcon />
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import communicationService from '@/services/PostMessageCommunation/CommunicationService';
import dataKeys from '@/services/PostMessageCommunation/enums/DataKeys';

import DownloadIcon from '../icons/DownloadIcon.vue';
import MenuTopIcon from '../icons/MenuTopIcon.vue';
import MenuDownIcon from '../icons/MenuDownIcon.vue';

export default {
  components: {
    DownloadIcon,
    MenuTopIcon,
    MenuDownIcon,
  },
  props: {
    videoSrc: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isShowVideo: true,
    }
  },
  methods: {
    async downloadVideo() {
      if(this.$store.state.appConfig.is_embedded_in_mh_app) {
        this.sendDownloadLinkToParent()
      } else {
        await this.downloadVideoNormal();
      }
    },
    sendDownloadLinkToParent() {
      const videoSource = this.videoSrc.replace('webm', 'mp4');
      communicationService.sendTriggerToParent({ key: dataKeys.MOBILE_APP_DOWNLOAD_VIDEO, value: videoSource });
    },
    async downloadVideoNormal() {
      try {
        const videoSource = this.videoSrc.replace('webm', 'mp4');

        if (this.$store.state.appConfig.windowWidth < 769) {
          window.open(videoSource, "_blank");
          return;
        }

        const response = await fetch(videoSource); // Videoyu indirmek için fetch API kullanılıyor
        const blob = await response.blob(); // Video içeriği blob olarak alınıyor
        const url = URL.createObjectURL(blob);

        // Yeni bir 'a' etiketi oluşturup indirme işlemini başlatıyoruz
        const link = document.createElement("a");
        link.href = url;
        link.download = this.getVideoNameFromSource(videoSource); // Dosya adı burada ayarlanıyor
        document.body.appendChild(link);
        link.click();

        // Bellek sızıntılarını önlemek için URL'yi serbest bırakıyoruz
        URL.revokeObjectURL(url);
        link.remove();
      } catch (error) {
        console.error("The video could not be downloaded.:", error);
      }
    },
    async shareVideo() {
      // videoSrc'in mp4 versiyonunu kullanacağımızı varsayıyoruz
      const videoSource = this.videoSrc.replace('webm', 'mp4');
      try {
        // Videoyu çekip blob olarak alıyoruz
        const response = await fetch(videoSource);
        if (!response.ok) {
          throw new Error("Network Error");
        }
        const blob = await response.blob();
        
        // Blob'u File nesnesine çeviriyoruz
        const fileName = this.getVideoNameFromSource(videoSource);
        const file = new File([blob], fileName, { type: blob.type });
        
        // Web Share API'nin dosya paylaşımını destekleyip desteklemediğini kontrol ediyoruz
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            files: [file],
            title: 'Share the Video',
            text: 'I want to share this video with you.',
          });
          
        } else if (navigator.share) {
          // Eğer dosya paylaşımı desteklenmiyorsa, URL veya diğer bilgileri paylaşabilirsiniz
          await navigator.share({
            title: 'Share the Video',
            text: 'I want to share this video with you.',
            url: videoSource
          });
         
        } else {
          alert("Unfortunately, your browser does not support the sharing API.");
        }
      } catch (error) {
        console.error("An error occurred while sharing the video:", error);
      }
    },
    getVideoNameFromSource(src) {
      const parts = src.split('/');
      const fileName = parts[parts.length - 1]; // Get the last part of the URL
      return fileName.split('?')[0]; // Remove any query parameters
    },

    handlePlay() {
      communicationService.sendDataToParent({
        key: dataKeys.STATISTICS,
        value: 'Iframe.ancientDnaAncientOriginsBreakdownPlayVideoClick',
      });
    },
  },
};
</script>

<style scoped>
.video-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0;
  width: 100%;
  background-color: var(--bg-body-color);
  border-radius: 8px;
  overflow: hidden;
  transition: max-height 0.5s ease-in;
  max-height: 600px !important;

  .video-player {
    width: 512px;
    height: 512px;
    background-color: var(--black-70);
    border: none;

    @media screen and (max-width: 1500px) {
      width: 412px;
      height: 412px;
    }

    @media screen and (max-width: 1200px) {
      width: 380px;
      height: 380px;
    }

    @media screen and (max-width: 769px) {
      width: 300px;
      height: 300px;
    }
  }

  .download-section {
    width: 100%;
    background-color: var(--black-70);
    color: white;
    text-align: center;
    padding: 0.5rem 0;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 0.5rem;

    .download-btn {
      text-decoration: none;
      color: white;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      font-size: 1rem;
      background: none;
      border: none;
      cursor: pointer;
      transition: color 0.3s;

      &:hover {
        color: #007bff;
      }

      i {
        font-size: 1.2rem;
      }
    }
  }
}

/* Transition Styling */
.hide-video-container {
  max-height: 0 !important;
  transition: max-height 0.5s ease-out;
  margin-top: -20px;
}

.toggle-section {
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 0;
  margin-top: 20px;
  background-color: var(--white-100);
  color: var(--black-40);

  .toggle-btn {
    cursor: pointer;
    font-size: 1rem;
  }
}
</style>
